import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../contexts/AuthContext'
import { authApiService } from '../services/authApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

export default function MfaVerifyPage() {
  const { refresh } = useAuth()
  const navigate = useNavigate()
  const [mode, setMode] = useState<'email' | 'recovery'>('email')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'recovery') {
        await authApiService.mfaRecover(code)
      } else {
        await authApiService.mfaVerify(code)
      }
      await refresh()
      navigate('/app/dashboard')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verification failed')
    } finally {
      setLoading(false)
    }
  }

  const isEmail = mode === 'email'

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.background,
      padding: spacing.md,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: radius.lg,
        boxShadow: shadow.lg,
        padding: spacing.xl,
        width: '100%',
        maxWidth: 360,
      }}>
        <div style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginBottom: spacing.xs }}>
          {isEmail ? 'Two-factor verification' : 'Recovery code'}
        </div>
        <div style={{ fontSize: fontSize.base, color: colors.textSecondary, marginBottom: spacing.xl }}>
          {isEmail
            ? 'Enter the 6-digit code from your authenticator app, or the code we emailed you.'
            : 'Enter one of your saved recovery codes.'}
        </div>

        {error && (
          <div style={{
            background: colors.errorLight,
            color: colors.error,
            border: `1px solid #fecaca`,
            borderRadius: radius.sm,
            padding: `${spacing.sm} ${spacing.md}`,
            fontSize: fontSize.sm,
            marginBottom: spacing.md,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: spacing.md }}>
            <label style={{
              display: 'block',
              fontSize: fontSize.sm,
              fontWeight: 500,
              color: colors.text,
              marginBottom: spacing.xs,
            }}>
              {isEmail ? 'Verification code' : 'Recovery code'}
            </label>
            <input
              key={mode}
              type="text"
              inputMode={isEmail ? 'numeric' : 'text'}
              value={code}
              onChange={e => setCode(isEmail ? e.target.value.replace(/\D/g, '') : e.target.value)}
              maxLength={isEmail ? 6 : 19}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: isEmail ? '20px' : fontSize.base,
                letterSpacing: isEmail ? '0.3em' : '0.1em',
                textAlign: 'center',
                color: colors.text,
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: isEmail ? 'inherit' : 'monospace',
              }}
              placeholder={isEmail ? '' : 'xxxx-xxxx-xxxx-xxxx'}
              required
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading || (isEmail && code.length < 6)}
            style={{
              width: '100%',
              padding: '10px',
              background: colors.primary,
              color: '#fff',
              border: 'none',
              borderRadius: radius.sm,
              fontSize: fontSize.base,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: spacing.lg }}>
          <button
            onClick={() => { setMode(isEmail ? 'recovery' : 'email'); setCode(''); setError('') }}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primary,
              fontSize: fontSize.sm,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {isEmail ? 'Use a recovery code instead' : 'Use email code instead'}
          </button>
        </div>
      </div>
    </div>
  )
}
